import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from '@mui/material'

import { useEffect, useState } from 'react'
import { getSectionAndBroadDropdown } from '../../../../api/SectionMasterApi.js'
import { useValidation } from '../../../../hooks/useValidation'
import ConfirmationDialog from '../../../common/ConfirmationDialog'
import CustomButton from '../../CustomButton'
import CustomInput from '../../CustomInput'
import CustomSelect from '../../CustomSelect'
import CustomMultiSelect from '../../CustomMultiSelect'
import { sectionMasterValidationRules } from '../configs/sectionMasterConfig'

const fields = [
  {
    name: 'section',
    label: 'Section Code',
    required: true,
    type: 'select',
    placeholder: 'e.g., 194A, 193',
  },
  {
    name: 'broadHead',
    label: 'Broad Head',
    required: true,
    type: 'select',
    placeholder: 'Enter broad head description',
  },
  { name: 'startDate', label: 'Start Date', required: true, type: 'date' },
  { name: 'endDate', label: 'End Date', required: false, type: 'date' },
  {
    name: 'applicableTo',
    label: 'Applicable To',
    required: true,
    type: 'select',
  },
  {
    name: 'payeeStatus',
    label: 'Payee Status',
    required: true,
    type: 'multiSelect',
  },
  {
    name: 'thresholdAnnual',
    label: 'Threshold Limit (Annual)',
    required: true,
    type: 'number',
    placeholder: 'Enter amount',
  },
  {
    name: 'thresholdTransaction',
    label: 'Threshold Limit (Transaction)',
    required: true,
    type: 'number',
    placeholder: 'Enter amount',
  },
  {
    name: 'prescribedRate',
    label: 'Prescribed Rate (%)',
    required: true,
    type: 'number',
    placeholder: 'Enter rate',
  },
]

const AddSection = ({
  open,
  onClose,
  onSave,
  formData = {},
  onChange,
  applicableToOptions = [],
  payeeStatusOptions = [],
  existingSections = [],
  onNotify = () => { },
  roleConfig
}) => {
  const [gapDialogOpen, setGapDialogOpen] = useState(false)
  const [pendingSave, setPendingSave] = useState(null)
  const [sectionOptions, setSectionOptions] = useState([])
  const [sectionMap, setSectionMap] = useState({})
  const [broadHeadOptions, setBroadHeadOptions] = useState([])

  // -------------------------
  // Fetch dropdown options
  // -------------------------
  const fetchSectionAndBroadDropdown = async () => {
    try {
      const response = await getSectionAndBroadDropdown()
      if (response?.success) {
        const sectionData = response.data || {}
        setSectionMap(sectionData)
        const sections = Object.keys(sectionData).map(code => ({ value: code, label: code }))
        setSectionOptions(sections)
      }
    } catch (err) {
      console.error('Error fetching dropdown:')
    }
  }

  useEffect(() => {
    if (roleConfig?.canCreate) {
      fetchSectionAndBroadDropdown()
    }
  }, [roleConfig])

  // -------------------------
  // Validation hook
  // -------------------------
  const {
    formData: validatedFormData,
    validateFormData,
    resetValidation,
    getFieldProps,
    handleFieldChange: originalHandleFieldChange,
  } = useValidation(sectionMasterValidationRules, formData, {
    validateOnBlur: true,
  })

  const handleFieldChange = (field, value) => {
    originalHandleFieldChange(field, value)
    if (onChange) {
      onChange(field)({ target: { value } })
    }
  }

  useEffect(() => {
    if (open) {
      resetValidation(formData)
    }
  }, [open, formData, resetValidation])

  // -------------------------
  // Helpers
  // -------------------------
  const parseDate = s => {
    if (!s) return null
    if (s instanceof Date && !isNaN(s.getTime())) return s
    if (typeof s === 'string') {
      const t = s.trim()
      if (!t) return null
      if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
        const [y, m, d] = t.split('-').map(Number)
        return new Date(y, m - 1, d)
      }
      if (/^\d{2}[\/.-]\d{2}[\/.-]\d{4}$/.test(t)) {
        const [d, m, y] = t.split(/[\/.-]/).map(Number)
        return new Date(y, m - 1, d)
      }
      const dt = new Date(t)
      return isNaN(dt.getTime()) ? null : dt
    }
    return null
  }

  const formatDate = d => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return ''
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  // Auto-fill Start Date ONLY if End Date exists

  const tryAutoFillStartDate = (sectionValRaw, broadHeadValRaw) => {
    const sectionVal = (sectionValRaw || '').toString().trim().toLowerCase()
    const broadHeadVal = (broadHeadValRaw || '').toString().trim().toLowerCase()
    if (!sectionVal || !broadHeadVal) return

    const list = Array.isArray(existingSections) ? existingSections : []
    const matches = list.filter(it => {
      const sec = (it.section ?? it.sectionCode ?? '')
        .toString()
        .trim()
        .toLowerCase()
      const bh = (it.broadHead ?? '').toString().trim().toLowerCase()
      return sec === sectionVal && bh === broadHeadVal
    })

    if (!matches.length) return

    // Only consider matches that have End Date
    const validEndDates = matches.map(m => parseDate(m.endDate)).filter(Boolean)

    if (!validEndDates.length) {
      // ❌ No End Date exists → show error and do NOT auto-populate Start Date
      onNotify({
        message: `Please add End Date for Section Code "${sectionValRaw}" and Broad Head "${broadHeadValRaw}" before adding a new one.`,
        severity: 'error',
      })
      handleFieldChange('startDate', '') // ensure startDate is empty
      return
    }

    // ✅ Auto-populate Start Date = latest End Date + 1
    const latestEnd = validEndDates.sort((a, b) => b - a)[0]
    const next = new Date(latestEnd)
    next.setDate(next.getDate() + 1)
    const nextStr = formatDate(next)

    handleFieldChange('startDate', nextStr)
    onNotify({
      message: `Start Date auto-filled as ${nextStr} (next day of latest End Date).`,
      severity: 'info',
    })
  }

  // -------------------------
  // Section change handler
  // -------------------------
  const handleSectionChange = value => {
    handleFieldChange('section', value)
    const broadHeads = sectionMap[value] || []
    setBroadHeadOptions(broadHeads)

    if (broadHeads.length === 1) {
      handleFieldChange('broadHead', broadHeads[0])
      tryAutoFillStartDate(value, broadHeads[0])
    } else {
      handleFieldChange('broadHead', '')
      handleFieldChange('startDate', '')
    }
  }

  // -------------------------
  // Save with overlap + gap checks
  // -------------------------
  const runSaveValidations = data => {
    const { startDate, endDate, section, broadHead, id } = data

    if (startDate && endDate && new Date(startDate) >= new Date(endDate)) {
      onNotify({
        message: 'Start Date must be less than End Date',
        severity: 'error',
      })
      return false
    }

    const secVal = (section || '').toString().trim().toLowerCase()
    const bhVal = (broadHead || '').toString().trim().toLowerCase()
    if (!(secVal && bhVal && startDate)) return true

    const newStart = parseDate(startDate)
    const newEnd = endDate ? parseDate(endDate) : new Date(9999, 11, 31)

    const candidates = (
      Array.isArray(existingSections) ? existingSections : []
    ).filter(it => {
      const sec = (it.section ?? it.sectionCode ?? '')
        .toString()
        .trim()
        .toLowerCase()
      const bh = (it.broadHead ?? '').toString().trim().toLowerCase()
      return sec === secVal && bh === bhVal
    })

    const overlap = candidates.some(it => {
      if (it.id === id) return false
      const exStart = parseDate(it.startDate)
      const exEnd = it.endDate ? parseDate(it.endDate) : new Date(9999, 11, 31)
      if (!exStart) return false
      return newStart <= exEnd && exStart <= newEnd
    })
    if (overlap) {
      onNotify({
        message:
          'Start Date and End Date are overlapping for same section and broadhead',
        severity: 'error',
      })
      return false
    }

    const latestEnd = candidates
      .map(m => parseDate(m.endDate))
      .filter(Boolean)
      .sort((a, b) => b - a)[0]
    if (latestEnd) {
      const expected = new Date(latestEnd)
      expected.setDate(expected.getDate() + 1)
      if (newStart.getTime() !== expected.getTime()) {
        setPendingSave(data)
        setGapDialogOpen(true)
        return false
      }
    }

    return true
  }

  const handleSave = () => {
    const validationResult = validateFormData()

    if (!validationResult.isValid) return
    if (runSaveValidations(validatedFormData)) {
      onSave(validatedFormData)
    }
  }

  const confirmGapProceed = () => {
    setGapDialogOpen(false)
    if (pendingSave) {
      onSave(pendingSave)
      setPendingSave(null)
    }
  }

  // -------------------------
  // Render fields
  // -------------------------
  const renderField = field => {
    const dynamicOptions =
      field.name === 'applicableTo'
        ? applicableToOptions
        : field.name === 'payeeStatus'
          ? payeeStatusOptions
          : field.options

    let extraProps = {}

    if (field.name === 'endDate') {
      if (validatedFormData.startDate && validatedFormData.endDate) {
        const start = new Date(validatedFormData.startDate)
        const end = new Date(validatedFormData.endDate)
        if (start >= end) {
          extraProps.helperText =
            'Section Start Date should be less than End Date.'
          extraProps.error = true
        }
      }
    }

    if (field.name === 'section') {
      return (
        <Grid item size={6} key={field.name}>
          <CustomSelect
            fullWidth
            label="Section Code"
            required
            placeholder="Section Code"
            options={sectionOptions}
            getOptionLabel={opt => opt.label}
            getOptionValue={opt => opt.value}
            value={validatedFormData.section || ''}
            onChange={e => handleSectionChange(e.target.value)}
            error={!!getFieldProps('section').error}
            helperText={getFieldProps('section').helperText}
          />
        </Grid>
      )
    }

    if (field.name === 'broadHead') {
      return (
        <Grid item size={6} key={field.name}>
          <CustomSelect
            fullWidth
            label="Broad Head"
            required
            placeholder="Enter broad head description"
            options={broadHeadOptions.map(bh => ({ value: bh, label: bh }))}
            getOptionLabel={opt => opt.label}
            getOptionValue={opt => opt.value}
            value={validatedFormData.broadHead || ''}
            onChange={e => {
              const val = e.target.value
              handleFieldChange('broadHead', val)
              tryAutoFillStartDate(validatedFormData.section, val)
            }}
            disabled={broadHeadOptions.length <= 1}
            error={!!getFieldProps('broadHead').error}
            helperText={getFieldProps('broadHead').helperText}
          />
        </Grid>
      )
    }

    if (field.name === 'startDate' || field.name === 'endDate') {
      const today = new Date()
      let minDate, maxDate

      if (field.name === 'startDate') {
        // Start Date: cannot be past, min = today or auto-filled date if later
        const autoFilledStart = parseDate(validatedFormData.startDate)
        minDate = autoFilledStart && autoFilledStart > today ? autoFilledStart : today
        maxDate = undefined // optional, can allow future dates
      }

      if (field.name === 'endDate') {
        const startDateVal = parseDate(validatedFormData.startDate)
        // End Date: cannot be before selected start date
        minDate = startDateVal ? startDateVal : today
        maxDate = undefined // optional, allow future dates
      }

      return (
        <Grid item size={6} key={field.name}>
          <CustomInput
            fullWidth
            label={field.label}
            required={field.required}
            type="date"
            placeholder="DD/MM/YYYY"
            value={validatedFormData[field.name] || ''}
            onChange={e => handleFieldChange(field.name, e.target.value)}
            error={!!getFieldProps(field.name).error || !!extraProps.error}
            helperText={getFieldProps(field.name).helperText || extraProps.helperText}

            slotProps={{
              htmlInput: {
                min: minDate ? formatDate(minDate) : undefined,
                max: maxDate ? formatDate(maxDate) : undefined,
              },
            }}
          />
        </Grid>
      )
    }

    if (field.type === "multiSelect") {
      return (
        <Grid item size={6} key={field.name}>
          <CustomMultiSelect
            fullWidth
            label={field.label}
            required={field.required}
            placeholder={field.placeholder || `Select ${field.label}`}
            options={dynamicOptions || []}
            getOptionLabel={opt => typeof opt === 'string' ? opt : (opt.label || opt.value || opt)}
            getOptionValue={opt => typeof opt === 'string' ? opt : (opt.value || opt.label || opt)}
            value={validatedFormData[field.name] ? validatedFormData[field.name].split(',').filter(Boolean) : []}
            onChange={e => {
              const selectedString = e.target.value.join(',')
              handleFieldChange(field.name, selectedString)
            }}
            error={!!getFieldProps(field.name).error}
            helperText={getFieldProps(field.name).helperText}
          />
        </Grid>
      )
    }

    if (field.type === "select") {
      return (
        <Grid item size={6} key={field.name}>
          <CustomSelect
            fullWidth={true}
            label={field.label}
            required={field.required}
            placeholder={field.placeholder || `Select ${field.label}`}
            options={dynamicOptions || []}
            getOptionLabel={opt => typeof opt === 'string' ? opt : (opt.label || opt.value || opt)}
            getOptionValue={opt => typeof opt === 'string' ? opt : (opt.value || opt.label || opt)}
            value={validatedFormData[field.name] || ''}
            onChange={e => handleFieldChange(field.name, e.target.value)}
            error={!!getFieldProps(field.name).error}
            helperText={getFieldProps(field.name).helperText}
          />
        </Grid>
      )
    }

    if (field.type === "number") {
      return (
        <Grid item size={6} key={field.name}>
          <CustomInput
            fullWidth
            label={field.label}
            required={field.required}
            type="number"
            placeholder={field.placeholder}
            value={validatedFormData[field.name] || ''}
            onChange={e => handleFieldChange(field.name, e.target.value)}
            error={!!getFieldProps(field.name).error}
            helperText={getFieldProps(field.name).helperText}
          
          />
        </Grid>
      )
    }

    return (
      <Grid item size={6} key={field.name}>
        <CustomInput
          label={field.label}
          required={field.required}
          type={field.type}
          placeholder={field.placeholder}
          value={validatedFormData[field.name] || ''}
          onChange={e => handleFieldChange(field.name, e.target.value)}
          error={!!getFieldProps(field.name).error}
          helperText={getFieldProps(field.name).helperText}
        />
      </Grid>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm">
      <Box sx={{ position: 'relative', p: 0 }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 28, pb: 0 }}>
          Add Section
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 16,
              top: 16,
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Typography
            sx={{ color: 'var(--forbrand-error)', mb: 2, fontWeight: 500, fontSize: 16 }}
          >
            (*) Fields are mandatory
          </Typography>
          <Grid container spacing={2}>
            {fields.map(renderField)}
          </Grid>
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}
          >
            <CustomButton onClick={handleSave} style={{ minWidth: 100 }}>
              Save
            </CustomButton>
          </Box>
        </DialogContent>

        <ConfirmationDialog
          open={gapDialogOpen}
          onClose={() => {
            setGapDialogOpen(false)
            setPendingSave(null)
          }}
          onConfirm={confirmGapProceed}
          type="warning"
          title="Gap detected between dates"
          message="There is a gap between the 'End Date' & 'Start Date' for which there will be no applicability of section & broadhead. Do you want to continue?"
          confirmText="Proceed"
          itemType=""
        />
      </Box>
    </Dialog>
  )
}

export default AddSection
